import { useState, useEffect } from "react"
import { Alert, Upload, Spin } from "antd"
import { InboxOutlined, CheckCircleFilled } from "@ant-design/icons"
import Dragger from "antd/es/upload/Dragger"

import { useAddBulkClientDocumentsMutation, useGetOnGoingBulkDocumentUploadMutation, useCompleteBulkDocumentUploadMutation } from "./crmDataApiSlice"
import { useSelector, useDispatch } from "react-redux"
import { selectCurrentId } from "../../auth/authSlice"
import { selectCrm } from "../crmSlice"

import { showSpinner, hideSpinner } from "../../../components/componentSlice"

import "./BulkClientDocument.css"

const BulkClientDocument = () => {
    const userId = useSelector(selectCurrentId)
    const crmId = useSelector(selectCrm)

    const dispatch = useDispatch()

    const [errorMessage, setErrorMessage] = useState(null)
    const [zipFile, setZipFile] = useState(null)
    const [activeBulkRequest, setActiveBulkRequest] = useState(null)
    const [isInitialLoadDone, setInitialLoadDone] = useState(false)

    const [addBulkClientDocuments, { isLoading: isAddBulkClientDocumentsLoading }] = useAddBulkClientDocumentsMutation()
    const [getOnGoingBulkDocumentUpload, { isLoading: isGetOnGoingBulkDocumentUploadLoading }] = useGetOnGoingBulkDocumentUploadMutation()
    const [completeBulkDocumentUpload, { isLoading: isCompleteBulkDocumentUploadLoading }] = useCompleteBulkDocumentUploadMutation()

    useEffect(() => {
        getOnGoingBulkDocumentUploadRequest(true)
    }, [])

    useEffect(() => {
        if(activeBulkRequest && activeBulkRequest.status === "IN_PROGRESS") {
            const interval = setInterval(getOnGoingBulkDocumentUploadRequest, 5000)
            return () => clearInterval(interval)
        }
    }, [activeBulkRequest])

    
    useEffect(() => {
        if (isAddBulkClientDocumentsLoading || isCompleteBulkDocumentUploadLoading) {
            dispatch(showSpinner())
        } else {
            dispatch(hideSpinner())
        }
    }, [isAddBulkClientDocumentsLoading, isCompleteBulkDocumentUploadLoading])

    const handleAddBulkClientDocuments = async () => {
        setErrorMessage(null)

        if(!zipFile) {
            return
        }

        try {
            const response = await addBulkClientDocuments({"crmId": crmId, "userId":userId, "zipFile": zipFile}).unwrap()
            if(response) {
                setActiveBulkRequest(response)
            }
        } catch(err) {
            if(!err.status || err.status === "FETCH_ERROR") {
                setErrorMessage("No Server Response")
            } else {
                setErrorMessage(err.data?.detail)
            }
        }
    }

    const getOnGoingBulkDocumentUploadRequest = async (isInitialLoad) => {
        try {
            const response = await getOnGoingBulkDocumentUpload(crmId).unwrap()
            if(response) {
                setActiveBulkRequest(response)
            }
        } catch(err) {
            if(!err.status || err.status === "FETCH_ERROR") {
                setErrorMessage("No Server Response")
            } else {
                setErrorMessage(err.data?.detail)
            }
        } finally {
            if(isInitialLoad) {
                setInitialLoadDone(true)
            }
        }
    }

    const completeOnGoingBulkDocumentUpload = async (id) => {
        setErrorMessage(null)
        try {
            await completeBulkDocumentUpload(id).unwrap()
            setActiveBulkRequest(null) 
            setZipFile(null)
        } catch(err) {
            if(!err.status || err.status === "FETCH_ERROR") {
                setErrorMessage("No Server Response")
            } else {
                setErrorMessage(err.data?.detail)
            }
        }
    }

    const createBulkUploadDocumentView = () => {
        if(!isInitialLoadDone) {
            return
        }

        return (
            <>
                <Alert
                    showIcon
                    description={
                        <>
                            <p>- This feature only accepts zip file with all the documents of the clients</p>
                            <p>- The documents inside the zip file must follow the format <strong>[LastName],[FirstName]-somedocument.pdf</strong> or else the document will not be uploaded</p>
                            <p>- The zip file can only be at a maximum size of <strong>200MB</strong></p>
                        </>
                    }
                    type="info"
                    banner
                />
                <div className="bulk-client-document-dragger-container">
                    <Dragger
                        className="bulk-client-document-dragger"
                        beforeUpload={(file) => {
                            const isZip =
                            file.type === 'application/zip' ||
                            file.name.toLowerCase().endsWith('.zip')

                            if (!isZip) {
                                return Upload.LIST_IGNORE
                            }

                            const isUnder200MB = file.size / 1024 / 1024 < 200

                            if (!isUnder200MB) {
                                setErrorMessage('ZIP file must be smaller than 200MB')
                                return Upload.LIST_IGNORE
                            }

                            return false
                        }}
                        fileList={zipFile}
                        maxCount={1}
                        multiple={false}
                        accept=".zip"
                        onChange={(info) => {
                            setErrorMessage(null)
                            setZipFile(info.fileList.slice(-1))
                        }}>
                        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                        <p className="ant-upload-text">Click or drag file to this area to upload</p>
                    </Dragger>
                </div>
                
                
                <div className="bulk-client-document-upload-button">
                    <button className="crm-button" disabled={!zipFile} onClick={() => handleAddBulkClientDocuments()}>Upload</button>     
                </div>  
            </>
        )
    }

    const createActiveBulkDocumentUploadView = () => {
        if(!isInitialLoadDone) {
            return
        }

        return (
            <>
                <div className="bulk-client-document-request-header">
                    <div className="bulk-client-document-request-id">
                        <p>Id: {activeBulkRequest.id}</p>
                    </div>
                </div>
                <div className="bulk-client-document-request-body">
                    {
                        activeBulkRequest.status === 'IN_PROGRESS' ? 
                            <>
                                <Spin size="large" description="Loading" spinning={true} />
                                <p>Uploading client documents...</p>
                            </>
                        :
                            <>
                                <CheckCircleFilled style={{ color: '#4cb121', fontSize: '50px' }} />
                                <p>Client documents are uploaded successfully.</p>
                            </>
                    }
                </div>
                {
                    activeBulkRequest.status === "UPLOADED" && 
                    <div className="bulk-client-document-control-container">
                        <div className="bulk-client-document-control-buttons">
                            <button className="crm-button" onClick={() => completeOnGoingBulkDocumentUpload(activeBulkRequest.id)}>Complete</button>
                        </div>
                    </div>
                }
            </> 
        )
    }

    return (
        <div className="table-page-container">
            {errorMessage && 
                <div className="support-error">
                    <p>{errorMessage}</p>
                </div>
            }
            <div className="forms-header">
                <div className='top-header'>
                    <header>Bulk Document Upload</header>
                </div>
            </div>
            <div className="bulk-client-document-request-card">
                {activeBulkRequest ? createActiveBulkDocumentUploadView() : createBulkUploadDocumentView() }          
            </div>
        </div>
    )
}

export default BulkClientDocument